import { LitElement, html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { sharedStyles } from '@lib/sharedStyles';

@customElement('millionaire-broadcast')
export default class MillionaireBroadcast extends LitElement {
  static styles = [
    sharedStyles,
    css`
      :host {
        display: block;
        width: 100%;
        height: 100%;
        background: transparent;
        color: black;
        font-family: var(--font-headline);
        overflow: hidden;
      }
      .container {
        display: flex;
        flex-direction: column;
        height: 100%;
        justify-content: center;
        align-items: center;
        padding: 2vmin;
        box-sizing: border-box;
        gap: 4vmin;
      }
      .question-box {
        background: #b1dcf1;
        border: var(--border-default);
        border-radius: var(--border-radius);
        padding: 4vmin 6vmin;
        text-align: center;
        font-size: clamp(2rem, 5vmin, 6rem);
        width: 90%;
        box-shadow: var(--shadow-default);
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 25vh;
        line-height: 1.2;
      }
      .options-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 3vmin;
        width: 95%;
        flex: 1;
        max-height: 50vh;
      }
      .option {
        border: var(--border-default);
        border-radius: var(--border-radius);
        padding: 2vmin 4vmin;
        font-size: clamp(1.5rem, 3.5vmin, 4rem);
        cursor: default;
        box-shadow: var(--shadow-default);
        display: flex;
        align-items: center;
        justify-content: flex-start;
        text-align: left;
        transition: transform 0.2s, filter 0.2s, box-shadow 0.2s;
        position: relative;
        background-color: white; /* Default fallback */
      }
      
      /* Specific colors for options 1-4 matching app palette */
      .option:nth-child(1) { background-color: #ffaaaa; }
      .option:nth-child(2) { background-color: #f2ed78; }
      .option:nth-child(3) { background-color: #9fe7cc; }
      .option:nth-child(4) { background-color: #c693e0; }

      .option-letter {
        font-weight: bold;
        margin-right: 2vmin;
        opacity: 0.5;
        min-width: 1.5em;
      }

      /* Selected state: scale up and brighten */
      .option.selected {
        transform: scale(1.02);
        filter: brightness(1.1);
        box-shadow: 0 10px 30px rgba(0,0,0,0.2);
        z-index: 10;
        border-width: 0.6vmin;
      }

      /* Correct state: Flash/Pulse */
      .option.correct {
        animation: pulseCorrect 1s infinite alternate;
        filter: brightness(1.1);
        border-width: 0.6vmin;
        border-color: #006400;
        /* Ensure background is clearly green if not already */
        background-color: #9fe7cc !important; 
      }
      
      /* Dim others when one is selected/correct */
      .options-grid.has-selection .option:not(.selected):not(.correct) {
        opacity: 0.4;
        filter: grayscale(0.5);
      }

      @keyframes pulseCorrect {
        from { transform: scale(1); box-shadow: 0 0 10px rgba(0,255,0,0.5); }
        to { transform: scale(1.03); box-shadow: 0 0 30px rgba(0,255,0,0.8); }
      }
    `
  ];

  @property({ type: Object }) question: any | null = null;
  @property({ type: String }) selectedOptionId: string | undefined;
  @property({ type: Boolean }) showAnswer: boolean = false;

  render() {
    if (!this.question) {
      return html`
            <div class="container" style="justify-content: center;">
                <div style="font-size: 4rem; text-transform:uppercase; color:#999; font-family: var(--font-headline);">Warten auf Frage...</div>
            </div>
        `;
    }

    const options = this.question.options || [];

    // Check if we have any active state to add a class to the grid container
    const hasSelection = !!this.selectedOptionId || this.showAnswer;

    return html`
      <div class="container">
        <div class="question-box">
            ${this.question.text}
        </div>
        <div class="options-grid ${hasSelection ? 'has-selection' : ''}">
            ${options.map((opt: any, idx: number) => {
      const letters = ['A', 'B', 'C', 'D'];
      const letter = letters[idx] || '';

      let classes = 'option';

      // State Logic
      if (this.showAnswer && opt.is_correct) {
        classes += ' correct';
      } else if (!this.showAnswer && this.selectedOptionId === opt.id) {
        classes += ' selected';
      }

      return html`
                    <div class="${classes}">
                        <span class="option-letter">${letter}:</span>
                        <span>${opt.text}</span>
                    </div>
                `;
    })}
        </div>
      </div>
    `;
  }
}
